import type { ReactNode } from 'react';
import { Fragment, useEffect, useMemo, useRef, useState } from 'react';

import type { A11y, Classnames, IconList } from '@snowit/icetypes';
import type { ButtonEvent } from '@snowit/icetypes/events';
import type { IButtonIcon } from '../ButtonIcon';

import useA11y from '@snowit/hooks/useA11y';
import useBreakpoints from '@snowit/hooks/useBreakpoints';
import Portal from '@snowit/hooks/usePortals';
import testId from '@snowit/utils/testId';

import Divider from '../../Divider';
import DropdownHeader from '../../utils/DropdownItems/DropdownHeader';
import DropdownItem from '../../utils/DropdownItems/DropdownItem';
import { m } from '../../utils/Motion';
import ButtonIcon from '../ButtonIcon';

import cx from 'classnames';
import s from './ButtonDropdown.module.scss';

export interface ButtonDropdownOption {
  key: string;
  icon?: IconList;
  color?: string;
  label?: string;
  hint?: string;
  onClick?: (...args: any[]) => void;
  loading?: boolean;
  disabled?: boolean;
  hidden?: boolean | (() => boolean);
  divider?: boolean;
  classNames?: Classnames<'item' | 'icon'>;
}

export interface IButtonDropdown extends IButtonIcon {
  component?: ReactNode | (() => ReactNode);
  options: ButtonDropdownOption[];
  id?: string;
  a11y?: A11y;
}

const ButtonDropdown = (props: IButtonDropdown) => {
  const { component, options, a11y } = props;
  const { minWidth } = useBreakpoints();
  const a11yH = useA11y();
  const [showDropdown, setShowDropdown] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);

  const dropdownId = a11yH.makeId('dropdown');

  const parentRef = useRef<HTMLSpanElement>(null);
  const itemsRef = useRef<(HTMLDivElement | null)[]>([]);

  const focusParent = () => {
    (parentRef.current?.firstElementChild as HTMLElement)?.focus();
  };

  let motionVariants = {};
  if (minWidth.sm) {
    motionVariants = {
      open: { opacity: 1, height: 'auto' },
      collapsed: { opacity: 0, height: 0 },
    };
  } else {
    motionVariants = {
      open: { opacity: 1, translateY: '0%' },
      collapsed: { opacity: 1, translateY: '100%' },
    };
  }

  const optionsList = useMemo(
    () =>
      options.filter((option) => {
        if (typeof option.hidden === 'function') return !option.hidden();
        return !option.hidden;
      }),
    [options],
  );

  useEffect(() => {
    if (itemsRef.current.length) {
      itemsRef.current[activeIndex]?.focus();
    }
  }, [activeIndex, itemsRef.current]);

  const handleOpen = () => {
    setShowDropdown((prev) => {
      const next = !prev;
      setActiveIndex(next ? 0 : -1);
      return next;
    });
  };

  const handleClose = (event: ButtonEvent) => {
    event.stopPropagation();
    setShowDropdown(false);
    setActiveIndex(-1);
    focusParent();
  };

  const handleItemClick = (item: ButtonDropdownOption, event: ButtonEvent) => {
    handleClose(event);
    item.onClick?.(item);
  };

  const renderButton = () => {
    if (typeof component === 'function') return component();
    if (component) return component;
    return (
      // OnClick on wrapper parent
      <ButtonIcon
        {...props}
        onClick={() => {}}
        icon={props.icon || 'more'}
        theme={props.theme || 'ghost'}
        size={props.size || 'db'}
        color={props.color || 'text'}
        a11y={{
          hidden: true,
          label: a11y?.label,
          haspopup: true,
        }}
      />
    );
  };

  const renderHeaderDropdown = () => {
    if (minWidth.sm) return null;
    return (
      <DropdownHeader
        classNames={{ wrapper: s.headerDropdown }}
        onClick={({ event }) => handleClose(event)}
      />
    );
  };

  const renderItems = (item: ButtonDropdownOption, index: number) => {
    const activeItem = index === activeIndex;

    if (item.divider) {
      return <Divider key={item.key} spacing={[0.5]} />;
    }

    return (
      <DropdownItem
        key={item.key}
        ref={(el) => {
          itemsRef.current[index] = el;
        }}
        classNames={{
          item: cx(s.item, item.classNames?.item, {
            [s.loadingItem]: item.loading,
          }),
          icon: cx(s.iconItem, item.classNames?.icon),
        }}
        icon={item.icon}
        color={item.color}
        disabled={item.disabled}
        loading={item.loading}
        label={item.label}
        hint={item.hint}
        aria-selected={activeItem}
        onClick={(e) => handleItemClick(item, e)}
        onKeyDown={(e) =>
          a11yH.handleListNav({
            event: e,
            active: activeIndex,
            total: optionsList.length,
            onEsc: handleClose,
            setIndex: setActiveIndex,
            onEnter: () => handleItemClick(item, e),
          })
        }
        a11y={{ role: 'option', tabIndex: activeItem ? 0 : -1 }}
        {...testId(item.key)}
      />
    );
  };

  const renderDropDown = () => {
    return (
      <m.div
        id={dropdownId}
        className={s.dropDown}
        initial='collapsed'
        animate='open'
        exit='collapsed'
        variants={motionVariants}
        transition={{
          type: 'tween',
          duration: 0.25,
          ease: [0.215, 0.61, 0.355, 1],
        }}
        role={'listbox'}
        aria-orientation={'vertical'}
      >
        {renderHeaderDropdown()}
        <div className={s.body} role={'menu'}>
          {optionsList.map(renderItems)}
        </div>
      </m.div>
    );
  };

  return (
    <Fragment>
      <span
        onClick={handleOpen}
        ref={parentRef}
        aria-expanded={showDropdown}
        aria-controls={dropdownId}
      >
        {renderButton()}
      </span>
      <Portal
        className={s.portal}
        mounted={showDropdown}
        content={renderDropDown()}
        bindTo={parentRef.current}
        position={'adaptive'}
        onClose={() => setShowDropdown(false)}
        fullscreen={!minWidth.sm}
        clickToHide
        // escToHide
      />
    </Fragment>
  );
};

export default ButtonDropdown;
